// file: src/pages/CreateDistributorInvoice.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  Heading,
  Text,
  Spinner,
  Button,
  HStack,
  VStack,
  Stack,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Input,
  Divider,
  Tooltip,
  FormControl,
  FormLabel,
  Select,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { DeleteIcon, EditIcon } from "@chakra-ui/icons";
import { FaFilePdf } from "react-icons/fa";
import { useCognito } from "../utils/useCognito";

const HOVER_SCALE = { transform: "scale(1)" };

// 🔌 Future endpoints (wired, but the fetch for products is mocked for now)
const GET_DISTRIBUTOR_COG_API =
  "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getDistributorCOG"; // <— new Lambda (future)
const CREATE_DISTRIBUTOR_INVOICE_API =
  "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/createDistributorInvoice"; // <— new Lambda (future)

// Pretty money
const money = (n) =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(Number(n || 0));

/**
 * Mock distributor data - replace with real API call later
 */
const DISTRIBUTORS = {
  "dist-001": "Acme Distribution Partners LLC",
  "dist-002": "Beta Medical Supply Corp",
  "dist-003": "Gamma Healthcare Solutions",
};

/**
 * Fetch distributor products + COG.
 * Spec: returns list with productId, name, cog, salePrice, sizes[]
 * For now: we return mocked data exactly as requested.
 */
async function fetchDistributorProducts(getToken) {
  // Put "real" call here so it's plug-and-play later
  try {
    const token = getToken();
    // This will be used soon; keeping it for structure
    // const res = await fetch(GET_DISTRIBUTOR_COG_API, {
    //   method: "POST",
    //   headers: {
    //     Authorization: `Bearer ${token}`,
    //     "Content-Type": "application/json",
    //   },
    //   body: JSON.stringify({}),
    // });
    // const json = await res.json();
    // if (!res.ok) throw new Error(json?.message || "Failed to load COG");

    // 🔧 Mocked response (as requested)
    return [
      {
        productId: "amnioamp",
        name: "AmnioAmp",
        cog: 500,
        salePrice: 800,
        sizes: ["1x1", "2x2", "2x3", "2x4", "4x4", "4x6", "4x8"],
      },
    ];
  } catch {
    // Even if the real call fails, we still return the mock so the UI works
    return [
      {
        productId: "amnioamp",
        name: "AmnioAmp",
        cog: 500,
        salePrice: 800,
        sizes: ["1x1", "2x2", "2x3", "2x4", "4x4", "4x6", "4x8"],
      },
    ];
  }
}

/**
 * Down payment percentage edit modal
 */
function DownPaymentModal({ isOpen, onClose, onConfirm, currentPercentage = 25 }) {
  const [percentage, setPercentage] = useState(currentPercentage);

  useEffect(() => {
    setPercentage(currentPercentage);
  }, [currentPercentage]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Down Payment Percentage</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Alert status="warning" mb={4}>
            <AlertIcon />
            Are you sure you want to change the down payment percentage?
          </Alert>
          <FormControl>
            <FormLabel>Down Payment Percentage</FormLabel>
            <NumberInput 
              min={0} 
              max={100} 
              value={percentage} 
              onChange={(_, n) => setPercentage(n || 0)}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="orange" onClick={() => onConfirm(Math.max(0, Math.min(100, Number(percentage) || 0)))}>
            Confirm Change
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/**
 * Quantity modal (reusable): lets user add or edit a line quantity
 */
function QuantityModal({ isOpen, onClose, onConfirm, defaultQty = 1, title = "Add Items" }) {
  const [qty, setQty] = useState(1); // Always start at 1

  useEffect(() => {
    if (isOpen) {
      setQty(defaultQty); // Only use defaultQty when modal opens (for edits)
    }
  }, [isOpen, defaultQty]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl>
            <FormLabel>Quantity</FormLabel>
            <NumberInput min={1} value={qty} onChange={(_, n) => setQty(n || 1)}>
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="teal" onClick={() => onConfirm(Math.max(1, Number(qty) || 1))} _hover={HOVER_SCALE}>
            Confirm
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

const CreateDistributorInvoice = () => {
  const { getIdToken } = useCognito();
  const toast = useToast();
  const printableContentRef = useRef();

  // Data
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);

  // Order meta
  const [distributorId, setDistributorId] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [downPaymentPercentage, setDownPaymentPercentage] = useState(25);

  // Cart: each line { productId, name, size, qty, cog, salePrice }
  const [lines, setLines] = useState([]);

  // Invoice preview state
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Modal states
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDownPaymentOpen, onOpen: onDownPaymentOpen, onClose: onDownPaymentClose } = useDisclosure();
  const [pendingLine, setPendingLine] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await fetchDistributorProducts(getIdToken);
        setProducts(data || []);
      } catch (err) {
        console.error(err);
        toast({
          title: "Error loading products",
          description: String(err.message || err),
          status: "error",
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [getIdToken, toast]);

  const addOrUpdateLine = (line, qty) => {
    setLines((prev) => {
      // if editing an existing index
      if (line.isEdit && typeof line.index === "number") {
        const next = [...prev];
        next[line.index] = { ...next[line.index], qty };
        return next;
      }
      // if adding and same product+size exists, increase qty
      const idx = prev.findIndex((l) => l.productId === line.productId && l.size === line.size);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: (Number(next[idx].qty) || 0) + qty };
        return next;
      }
      return [...prev, { ...line, qty }];
    });
  };

  const removeLine = (index) => {
    setLines((prev) => prev.filter((_, i) => i !== index));
  };

  const totals = useMemo(() => {
    const subtotal = lines.reduce((sum, l) => sum + (Number(l.salePrice) || 0) * (Number(l.qty) || 0), 0);
    const cogTotal = lines.reduce((sum, l) => sum + (Number(l.cog) || 0) * (Number(l.qty) || 0), 0);
    const margin = subtotal - cogTotal;
    const marginPct = subtotal > 0 ? (margin / subtotal) * 100 : 0;
    return { subtotal, cogTotal, margin, marginPct };
  }, [lines]);

  const handleClickSize = (p, size) => {
    setPendingLine({
      productId: p.productId,
      name: p.name,
      size,
      cog: p.cog,
      salePrice: p.salePrice,
      isEdit: false,
    });
    onOpen();
  };

  const handleEditQty = (index) => {
    const line = lines[index];
    setPendingLine({
      ...line,
      isEdit: true,
      index,
    });
    onOpen();
  };

  const handleConfirmQty = (qty) => {
    if (!pendingLine) return;
    addOrUpdateLine(pendingLine, qty);
    onClose();
    setPendingLine(null);
    toast({
      title: pendingLine.isEdit ? "Quantity updated" : "Added to order",
      description: `${pendingLine.name} • ${pendingLine.size} × ${qty}`,
      status: "success",
      duration: 1800,
      isClosable: true,
    });
  };

  const handleDownPaymentChange = (newPercentage) => {
    setDownPaymentPercentage(newPercentage);
    onDownPaymentClose();
    toast({
      title: "Down payment percentage updated",
      description: `Set to ${newPercentage}%`,
      status: "success",
      duration: 1800,
      isClosable: true,
    });
  };

  const createInvoicePreview = () => {
    if (!lines.length) {
      toast({
        title: "No items in order",
        description: "Add at least one item to proceed.",
        status: "warning",
        isClosable: true,
      });
      return;
    }

    const invoiceId = `DIST-${Date.now()}`;
    const currentDate = new Date().toISOString();
    const lineItems = lines.map((l) => ({
      label: `${l.name} • ${l.size}`,
      quantity: Number(l.qty),
      unitPrice: Number(l.salePrice),
      total: Number(l.salePrice) * Number(l.qty),
    }));

    const subtotal = totals.subtotal;
    const downPaymentAmount = (subtotal * downPaymentPercentage) / 100;

    const preview = {
      invoiceId,
      orderCreatedDate: currentDate,
      dueDate: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString(), // 60 days from now
      paid: false,
      lineItems,
      totalDue: downPaymentAmount,
      subtotal,
      downPaymentPercentage,
      fullAmount: subtotal,
      distributorId,
      distributorName: DISTRIBUTORS[distributorId] || distributorId || "Not specified",
      poNumber,
      notes,
    };

    setInvoicePreview(preview);
    setShowPreview(true);

    toast({
      title: "Invoice preview created",
      description: "Review the invoice below and submit when ready.",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const generatePDF = () => {
    if (!invoicePreview || !printableContentRef.current) return;

    const content = printableContentRef.current.innerHTML;
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    iframe.style.left = '-9999px';
    iframe.style.top = '-9999px';
    
    document.body.appendChild(iframe);
    
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    
    doc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Distributor Invoice ${invoicePreview.invoiceId}</title>
          <link href="https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;600&display=swap" rel="stylesheet">
          <style>
            @media print {
              @page {
                margin: 0.75in;
                size: letter;
              }
            }
            body {
              font-family: 'Rajdhani', sans-serif;
              margin: 0;
              padding: 24px;
              color: #1e1e1e;
              line-height: 1.4;
              font-size: 14px;
              background: white;
            }
            .invoice-header {
              display: flex;
              justify-content: center;
              align-items: center;
              margin-bottom: 20px;
              border-bottom: 2px solid #207d87;
              padding-bottom: 16px;
            }
            .logo-container img {
              height: 45px;
              width: auto;
            }
            h3 {
              text-align: center;
              font-size: 20px;
              margin: 16px 0;
              color: #207d87;
              font-weight: 600;
            }
            .invoice-details {
              margin-bottom: 16px;
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 8px;
            }
            .invoice-details div {
              margin-bottom: 6px;
            }
            .invoice-details strong {
              color: #3d3d3d;
              font-weight: 600;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 16px 0;
              font-size: 13px;
            }
            th, td {
              border: 1px solid #d0d0d0;
              padding: 8px 10px;
              text-align: left;
            }
            th {
              background-color: #f8f9fa;
              color: #3d3d3d;
              font-weight: 600;
            }
            .text-right {
              text-align: right;
            }
            .badge-paid {
              background-color: #207d87;
              color: white;
              padding: 3px 8px;
              border-radius: 4px;
              font-size: 12px;
            }
            .badge-unpaid {
              background-color: #c93c3c;
              color: white;
              padding: 3px 8px;
              border-radius: 4px;
              font-size: 12px;
            }
            .totals-section {
              margin-top: 20px;
              text-align: right;
              font-weight: 600;
              font-size: 14px;
              border-top: 2px solid #207d87;
              padding-top: 16px;
            }
            .totals-section div {
              margin-bottom: 6px;
            }
            .down-payment {
              font-size: 18px;
              color: #207d87;
              font-weight: 700;
            }
            .footer {
              text-align: center;
              margin-top: 32px;
              font-size: 12px;
              color: #666;
              border-top: 1px solid #e2e8f0;
              padding-top: 16px;
            }
            .footer img {
              margin-top: 8px;
              height: 50px;
            }
            .wire-instructions {
              margin-top: 20px;
              font-size: 11px;
              color: #555;
              text-align: center;
            }
            .wire-instructions a {
              color: #207d87;
              text-decoration: none;
              word-break: break-all;
            }
          </style>
        </head>
        <body>
          <div class="invoice-header">
            <div class="logo-container">
              <img src="https://www.curagenesis.com/assets/img/cglogotext.webp" alt="CuraGenesis Logo" />
            </div>
          </div>

          <h3>Distributor Invoice</h3>

          <div class="invoice-details">
            <div><strong>Invoice ID:</strong> ${invoicePreview.invoiceId}</div>
            <div><strong>Order Date:</strong> ${new Date(invoicePreview.orderCreatedDate).toLocaleDateString()}</div>
            <div><strong>Distributor:</strong> ${invoicePreview.distributorName}</div>
            <div><strong>Due Date:</strong> ${new Date(invoicePreview.dueDate).toLocaleDateString()}</div>
            ${invoicePreview.poNumber ? `<div><strong>PO Number:</strong> ${invoicePreview.poNumber}</div>` : '<div></div>'}
            <div><strong>Payment Status:</strong> <span class="badge-unpaid">Unpaid</span></div>
            ${invoicePreview.notes ? `<div style="grid-column: 1 / -1;"><strong>Notes:</strong> ${invoicePreview.notes}</div>` : ''}
          </div>

          <table>
            <thead>
              <tr>
                <th>Item Description</th>
                <th class="text-right">Qty</th>
                <th class="text-right">Unit Price</th>
                <th class="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              ${invoicePreview.lineItems.map(item => `
                <tr>
                  <td>${item.label}</td>
                  <td class="text-right">${item.quantity}</td>
                  <td class="text-right">$${Number(item.unitPrice).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td class="text-right">$${Number(item.total).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="totals-section">
            <div>Subtotal: $${Number(invoicePreview.subtotal).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
            <div class="down-payment">Down Payment Due (${invoicePreview.downPaymentPercentage}%): $${Number(invoicePreview.totalDue).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
            <div style="margin-top: 12px; font-size: 12px; color: #666;">Remaining Balance: $${Number(invoicePreview.fullAmount - invoicePreview.totalDue).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
          </div>

          <div class="wire-instructions">
            <p>For wire transfer instructions, please visit:</p>
            <a href="https://www.curagenesis.com/assets/pdf/CuraGenesis_Wire_Transfer_Instructions.pdf" target="_blank">
              https://www.curagenesis.com/assets/pdf/CuraGenesis_Wire_Transfer_Instructions.pdf
            </a>
          </div>

          <div class="footer">
            <p>Thank you for your partnership!</p>
            <img src="https://www.curagenesis.com/assets/img/dnahelix.webp" alt="CuraGenesis DNA Logo" />
          </div>
        </body>
      </html>
    `);
    
    doc.close();
    
    iframe.onload = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        
        toast({
          title: "PDF generation initiated",
          description: "Use your browser's print dialog to save as PDF.",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
      } catch (err) {
        console.error('Print failed:', err);
        toast({
          title: "Print failed",
          description: "Unable to generate PDF. Please try again.",
          status: "error",
          isClosable: true,
        });
      } finally {
        // Cleanup
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      }
    };
  };

  const submitInvoice = async () => {
    if (!invoicePreview) return;

    try {
      const token = getIdToken();

      const payload = {
        ...invoicePreview,
        lineItems: lines.map((l) => ({
          productId: l.productId,
          name: l.name,
          size: l.size,
          quantity: Number(l.qty),
          cog: Number(l.cog),
          salePrice: Number(l.salePrice),
        })),
      };

      const res = await fetch(CREATE_DISTRIBUTOR_INVOICE_API, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        throw new Error(json.message || "Create distributor invoice failed");
      }

      toast({
        title: "Distributor invoice submitted successfully",
        description: `Invoice ID: ${json.invoiceId || invoicePreview.invoiceId}`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Reset form
      setLines([]);
      setInvoicePreview(null);
      setShowPreview(false);
      setDistributorId("");
      setPoNumber("");
      setNotes("");
      setDownPaymentPercentage(25);

    } catch (err) {
      console.error(err);
      toast({
        title: "Submission error",
        description: String(err.message || err),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return (
      <Box p={[4, 6, 8]}>
        <Heading size="lg" mb={3}>
          Create Distributor Invoice
        </Heading>
        <Spinner size="lg" />
      </Box>
    );
  }

  const single = products[0]; // current mock is single product (amnioamp)

  return (
    <Box p={[4, 6, 8]}>
      <Heading size="lg" mb={2}>
        Create Distributor Invoice
      </Heading>
      <Text color="gray.600" mb={6}>
        Pick sizes and quantities. We'll compute totals and margin; submission will call the new Lambda (stubbed for now).
      </Text>

      {/* Order meta */}
      <Stack spacing={4} direction={{ base: "column", md: "row" }} mb={6}>
        <FormControl maxW={{ md: "280px" }}>
          <FormLabel>Distributor</FormLabel>
          <Select
            placeholder="Select distributor"
            value={distributorId}
            onChange={(e) => setDistributorId(e.target.value)}
          >
            {Object.entries(DISTRIBUTORS).map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </Select>
        </FormControl>

        <FormControl maxW={{ md: "200px" }}>
          <FormLabel>PO Number</FormLabel>
          <Input
            value={poNumber}
            onChange={(e) => setPoNumber(e.target.value)}
            placeholder="Optional"
          />
        </FormControl>

        <FormControl maxW={{ md: "200px" }}>
          <FormLabel>Down Payment %</FormLabel>
          <HStack>
            <Text fontSize="lg" fontWeight="semibold" color="teal.600">
              {downPaymentPercentage}%
            </Text>
            <Tooltip label="Edit down payment percentage">
              <IconButton
                aria-label="Edit percentage"
                icon={<EditIcon />}
                size="sm"
                variant="ghost"
                onClick={onDownPaymentOpen}
              />
            </Tooltip>
          </HStack>
        </FormControl>

        <FormControl flex="1">
          <FormLabel>Notes</FormLabel>
          <Input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional internal note…"
          />
        </FormControl>
      </Stack>

      {/* Product picker */}
      <Box borderWidth="1px" borderRadius="lg" p={4} mb={6}>
        <HStack justify="space-between" align="flex-start" mb={3}>
          <VStack align="start" spacing={0}>
            <Heading size="md">{single?.name || "Product"}</Heading>
            <Text color="gray.600" fontSize="sm">{single?.productId}</Text>
          </VStack>
          <HStack>
            <Badge colorScheme="purple">COG: {money(single?.cog)}</Badge>
            <Badge colorScheme="green">Sale: {money(single?.salePrice)}</Badge>
          </HStack>
        </HStack>

        <Divider my={3} />

        <Text fontWeight="semibold" mb={2}>Sizes</Text>
        <HStack wrap="wrap" spacing={2}>
          {single?.sizes?.map((sz) => (
            <Button
              key={sz}
              size="sm"
              variant="outline"
              onClick={() => handleClickSize(single, sz)}
              _hover={HOVER_SCALE}
            >
              Add {sz}
            </Button>
          ))}
        </HStack>
      </Box>

      {/* Cart table */}
      <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
        <Table size="sm" variant="striped">
          <Thead>
            <Tr>
              <Th>Product</Th>
              <Th>Size</Th>
              <Th isNumeric>Qty</Th>
              <Th isNumeric>Unit (Sale)</Th>
              <Th isNumeric>Line Total</Th>
              <Th isNumeric>Unit COG</Th>
              <Th isNumeric>COG Total</Th>
              <Th textAlign="right">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {lines.length === 0 ? (
              <Tr>
                <Td colSpan={8}>
                  <Text color="gray.500">No items yet. Add sizes above.</Text>
                </Td>
              </Tr>
            ) : (
              lines.map((l, i) => {
                const lineTotal = (Number(l.salePrice) || 0) * (Number(l.qty) || 0);
                const cogTotal = (Number(l.cog) || 0) * (Number(l.qty) || 0);
                return (
                  <Tr key={`${l.productId}-${l.size}-${i}`}>
                    <Td>{l.name}</Td>
                    <Td>{l.size}</Td>
                    <Td isNumeric>{l.qty}</Td>
                    <Td isNumeric>{money(l.salePrice)}</Td>
                    <Td isNumeric>{money(lineTotal)}</Td>
                    <Td isNumeric>{money(l.cog)}</Td>
                    <Td isNumeric>{money(cogTotal)}</Td>
                    <Td textAlign="right">
                      <HStack spacing={1} justify="flex-end">
                        <Tooltip label="Edit quantity">
                          <IconButton
                            aria-label="Edit qty"
                            icon={<EditIcon />}
                            size="xs"
                            variant="ghost"
                            onClick={() => handleEditQty(i)}
                            _hover={HOVER_SCALE}
                          />
                        </Tooltip>
                        <Tooltip label="Remove line">
                          <IconButton
                            aria-label="Remove"
                            icon={<DeleteIcon />}
                            size="xs"
                            variant="ghost"
                            onClick={() => removeLine(i)}
                            _hover={HOVER_SCALE}
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                );
              })
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Totals */}
      <HStack justify="flex-end" mt={4}>
        <VStack align="end" spacing={1}>
          <HStack>
            <Text color="gray.600">Subtotal:</Text>
            <Text fontWeight="semibold">{money(totals.subtotal)}</Text>
          </HStack>
          <HStack>
            <Text color="gray.600">COG Total:</Text>
            <Text fontWeight="semibold">{money(totals.cogTotal)}</Text>
          </HStack>
          <HStack>
            <Text color="gray.600">Margin:</Text>
            <Text fontWeight="semibold">{money(totals.margin)}</Text>
            <Badge colorScheme={totals.margin >= 0 ? "green" : "red"}>
              {totals.marginPct.toFixed(1)}%
            </Badge>
          </HStack>
          <HStack>
            <Text color="gray.600">Down Payment ({downPaymentPercentage}%):</Text>
            <Text fontWeight="semibold" color="teal.600">
              {money((totals.subtotal * downPaymentPercentage) / 100)}
            </Text>
          </HStack>
        </VStack>
      </HStack>

      <HStack justify="space-between" mt={6}>
        <Button
          variant="outline"
          onClick={() => {
            setLines([]);
            setInvoicePreview(null);
            setShowPreview(false);
          }}
          _hover={HOVER_SCALE}
          isDisabled={!lines.length}
        >
          Clear Order
        </Button>
        <Button
          colorScheme="teal"
          onClick={createInvoicePreview}
          _hover={HOVER_SCALE}
          isDisabled={showPreview}
        >
          Create Invoice Preview
        </Button>
      </HStack>

      {/* Invoice Preview */}
      {showPreview && invoicePreview && (
        <Box mt={8} borderWidth="2px" borderColor="teal.200" borderRadius="lg" p={6} bg="gray.50">
          <HStack justify="space-between" align="center" mb={4}>
            <Heading size="md" color="teal.600">Invoice Preview</Heading>
            <Button
              leftIcon={<FaFilePdf />}
              colorScheme="blue"
              size="sm"
              onClick={generatePDF}
              _hover={HOVER_SCALE}
            >
              Save as PDF
            </Button>
          </HStack>
          
          <Box ref={printableContentRef} bg="white" borderRadius="md" p={4} mb={4}>
            <Box textAlign="center" mb={4}>
              <img
                src="https://www.curagenesis.com/assets/img/cglogotext.webp"
                alt="CuraGenesis Logo"
                style={{ height: "40px", width: "auto", margin: "0 auto" }}
              />
            </Box>

            <Box mb={4}>
              <Text><strong>Invoice ID:</strong> {invoicePreview.invoiceId}</Text>
              <Text><strong>Distributor:</strong> {invoicePreview.distributorName}</Text>
              {poNumber && <Text><strong>PO Number:</strong> {poNumber}</Text>}
              <Text><strong>Order Date:</strong> {new Date(invoicePreview.orderCreatedDate).toLocaleDateString()}</Text>
              <Text><strong>Due Date:</strong> {new Date(invoicePreview.dueDate).toLocaleDateString()}</Text>
              {notes && <Text><strong>Notes:</strong> {notes}</Text>}
            </Box>

            <Table variant="simple" size="sm" mb={4}>
              <Thead>
                <Tr>
                  <Th>Item</Th>
                  <Th isNumeric>Qty</Th>
                  <Th isNumeric>Unit Price</Th>
                  <Th isNumeric>Total</Th>
                </Tr>
              </Thead>
              <Tbody>
                {invoicePreview.lineItems.map((item, idx) => (
                  <Tr key={idx}>
                    <Td>{item.label}</Td>
                    <Td isNumeric>{item.quantity}</Td>
                    <Td isNumeric>{money(item.unitPrice)}</Td>
                    <Td isNumeric>{money(item.total)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>

            <Box textAlign="right">
              <Text><strong>Subtotal:</strong> {money(invoicePreview.subtotal)}</Text>
              <Text fontSize="lg" fontWeight="bold" color="teal.600">
                <strong>Down Payment Due ({downPaymentPercentage}%):</strong> {money(invoicePreview.totalDue)}
              </Text>
              <Text fontSize="sm" color="gray.600" mt={2}>
                Remaining Balance: {money(invoicePreview.fullAmount - invoicePreview.totalDue)}
              </Text>
            </Box>
          </Box>

          <HStack justify="space-between">
            <Button
              variant="outline"
              onClick={() => {
                setInvoicePreview(null);
                setShowPreview(false);
              }}
            >
              Edit Order
            </Button>
            <Button
              colorScheme="green"
              onClick={submitInvoice}
              _hover={HOVER_SCALE}
            >
              Submit Invoice
            </Button>
          </HStack>
        </Box>
      )}

      {/* Quantity modal */}
      <QuantityModal
        isOpen={isOpen}
        onClose={() => {
          onClose();
          setPendingLine(null);
        }}
        onConfirm={handleConfirmQty}
        defaultQty={pendingLine?.isEdit ? pendingLine.qty : 1}
        title={pendingLine?.isEdit ? "Update Quantity" : "Add Items"}
      />

      {/* Down payment modal */}
      <DownPaymentModal
        isOpen={isDownPaymentOpen}
        onClose={onDownPaymentClose}
        onConfirm={handleDownPaymentChange}
        currentPercentage={downPaymentPercentage}
      />
    </Box>
  );
};

export default CreateDistributorInvoice;